import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { calendarService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { ICalendarLockConfig, ICalendarLockResponse } from "@miplatform/types";

const LOCK_RENEWAL_BUFFER_MS = 5 * 1000;
const lockRefreshInterval = 5 * 1000; // 5 seconds
const LOCK_RETRY_DELAY_MS = 5 * 1000;
const CalendarLockContext = createContext<CalendarLockContextType | undefined>(
  undefined
);

export const CalendarLockProvider: React.FC<CalendarLockProviderProps> = ({
  children,
}) => {
  const { params } = useRouter();
  const { calendarSetId } = params;
  const [isCalendarLocked, setIsCalendarLocked] = useState(false);
  const [lockDetails, setLockDetails] = useState<ICalendarLockConfig | null>(
    null
  );
  const [lockAcquiredState, setLockAcquiredState] =
    useState<ICalendarLockResponse | null>(null);
  const isAcquiringLockRef = useRef(false);

  const getCalendarSetLockStatus = async () => {
    if (!calendarSetId) {
      return;
    }
    const response = await calendarService.calendarSets.getLockStatus({
      id: calendarSetId,
    });
    if (response.status === HTTP_STATUSES.OK) {
      setIsCalendarLocked(response.data.isLocked);
      if (response.data.isLocked) {
        setLockDetails({
          lockedByUserId: response.data.lockedByUserId,
          lockedByUserNameDisplayName: response.data.lockedByUserDisplayName,
          lockedUntil: response.data.lockedUntil,
          message: response.data.message,
        });
      } else {
        setLockDetails(null);
      }
    }
  };

  useEffect(() => {
    getCalendarSetLockStatus();
    const intervalId = setInterval(() => {
      getCalendarSetLockStatus();
    }, lockRefreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const acquireCalendarLock = useCallback(
    async (onLockFailure?: () => void) => {
      if (!calendarSetId || isAcquiringLockRef.current) {
        return null;
      }

      isAcquiringLockRef.current = true;
      try {
        const response = await calendarService.calendarSets.acquireLock({
          id: calendarSetId,
        });
        if (response.status === HTTP_STATUSES.OK) {
          setLockAcquiredState(response.data);
          if (!response.data.lockAcquired) {
            setIsCalendarLocked(true);
            toastService.error({ text: response.data.message });
            if (onLockFailure) {
              onLockFailure();
            }
            setLockDetails({
              lockedByUserId: response.data.lockedByUserId,
              lockedByUserNameDisplayName:
                response.data.lockedByUserDisplayName,
              lockedUntil: response.data.lockedUntil,
              message: response.data.message,
            });
          }
          return response.data;
        }

        return null;
      } finally {
        isAcquiringLockRef.current = false;
      }
    },
    [calendarSetId]
  );

  const createCalendarLockRenewal = useCallback(
    (onLockFailure?: () => void) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let isCancelled = false;

      const scheduleNextRenewal = (delayMs: number) => {
        timeoutId = setTimeout(() => {
          renewLock().catch(() => {
            if (!isCancelled) {
              scheduleNextRenewal(LOCK_RETRY_DELAY_MS);
            }
          });
        }, delayMs);
      };

      const renewLock = async () => {
        if (isCancelled) {
          return;
        }

        const lockResponse = await acquireCalendarLock(onLockFailure);
        if (isCancelled) {
          return;
        }

        const lockedUntilMs = lockResponse?.lockedUntil
          ? new Date(lockResponse.lockedUntil).getTime()
          : Number.NaN;

        const renewalDelayMs = Number.isNaN(lockedUntilMs)
          ? LOCK_RETRY_DELAY_MS
          : lockedUntilMs - Date.now() - LOCK_RENEWAL_BUFFER_MS;

        scheduleNextRenewal(
          renewalDelayMs > 0 ? renewalDelayMs : LOCK_RETRY_DELAY_MS
        );
      };

      renewLock().catch(() => {
        if (!isCancelled) {
          scheduleNextRenewal(LOCK_RETRY_DELAY_MS);
        }
      });

      return () => {
        isCancelled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    },
    [acquireCalendarLock]
  );

  const calendarLockValue = useMemo(
    () => ({
      isCalendarLocked,
      lockDetails,
      lockAcquiredState,
      getCalendarSetLockStatus,
      createCalendarLockRenewal,
    }),
    [isCalendarLocked, lockDetails, lockAcquiredState]
  );

  return (
    <CalendarLockContext.Provider value={calendarLockValue}>
      {children}
    </CalendarLockContext.Provider>
  );
};

export const useCalendarLockContext = (): CalendarLockContextType => {
  const context = useContext(CalendarLockContext);
  if (context === undefined) {
    throw new Error(
      "useCalendarLockContext must be used within CalendarLockProvider"
    );
  }
  return context;
};

interface CalendarLockContextType {
  isCalendarLocked: boolean;
  lockDetails: ICalendarLockConfig | null;
  lockAcquiredState: ICalendarLockResponse | null;
  getCalendarSetLockStatus: () => void;
  createCalendarLockRenewal: (onLockFailure?: () => void) => () => void;
}

interface CalendarLockProviderProps {
  children: ReactNode;
}
